import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import {
  getLeave,
  getLeaveTypes,
  createLeave,
  updateLeave,
  suggestReturningDate,
  type ILeaveType,
} from '@/api/leave.api';
import { buildErrorToast } from '@/lib/apiError';
import { getEmployees, getEmployee, type IEmployee } from '@/api/employee.api';
import { uploadFile, ACCEPT_STRING, ALLOWED_EXTENSIONS, MAX_FILE_SIZE } from '@/api/file.api';
import { AlertTriangle, ArrowLeft, Upload, FileText, X, Loader2 } from 'lucide-react';

const leaveSchema = z.object({
  staffId: z.string().min(1, 'Staff is required'),
  leaveTypeId: z.string().min(1, 'Leave type is required'),
  fromDate: z.string().min(1, 'From date is required'),
  toDate: z.string().min(1, 'To date is required'),
  halfDayEnabled: z.boolean(),
  halfDay: z.enum(['AM', 'PM']).nullable().optional(),
  returningDate: z.string().min(1, 'Returning date is required'),
  reason: z.string().optional(),
  attachmentUrl: z.string().optional(),
  note: z.string().optional(),
});

type LeaveFormValues = z.infer<typeof leaveSchema>;

export function LeaveFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const prefillStaffId = searchParams.get('staffId');
  const isEdit = !!id;
  const queryClient = useQueryClient();

  const { toast } = useToast();
  const errToast = buildErrorToast(toast);

  const [overlapDialogOpen, setOverlapDialogOpen] = useState(false);
  const [pendingSubmitValues, setPendingSubmitValues] = useState<LeaveFormValues | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [staffSearch, setStaffSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [staffDropdownOpen, setStaffDropdownOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<IEmployee | null>(null);
  const staffInputRef = useRef<HTMLInputElement>(null);
  const staffBlurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: leaveTypes } = useQuery({
    queryKey: ['leave-types'],
    queryFn: getLeaveTypes,
  });

  const { data: existingLeave } = useQuery({
    queryKey: ['leave', id],
    queryFn: () => getLeave(id!),
    enabled: isEdit,
  });

  // Debounce staff search → API call
  useEffect(() => {
    const timerId = setTimeout(() => setDebouncedSearch(staffSearch), 300);
    return () => clearTimeout(timerId);
  }, [staffSearch]);

  const { data: staffSearchData, isFetching: staffFetching } = useQuery({
    queryKey: ['employees-for-leave', debouncedSearch],
    queryFn: () => getEmployees({ search: debouncedSearch || undefined, limit: 20 }),
    enabled: staffDropdownOpen,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
  const staffResults = staffSearchData?.data ?? [];

  // Fetch prefill employee for display (field is disabled, just need the name)
  const { data: prefillEmployee } = useQuery({
    queryKey: ['employee', prefillStaffId],
    queryFn: () => getEmployee(prefillStaffId!),
    enabled: !!prefillStaffId,
  });
  useEffect(() => {
    if (prefillEmployee) setSelectedEmployee(prefillEmployee);
  }, [prefillEmployee]);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<LeaveFormValues>({
    resolver: zodResolver(leaveSchema) as import('react-hook-form').Resolver<LeaveFormValues>,
    defaultValues: {
      halfDayEnabled: false,
      halfDay: null,
      staffId: prefillStaffId || '',
    },
  });

  // Populate form on edit
  useEffect(() => {
    if (existingLeave) {
      reset({
        staffId: existingLeave.staffId,
        leaveTypeId: existingLeave.leaveTypeId,
        fromDate: existingLeave.fromDate.split('T')[0],
        toDate: existingLeave.toDate.split('T')[0],
        halfDayEnabled: !!existingLeave.halfDay,
        halfDay: existingLeave.halfDay || null,
        returningDate: existingLeave.returningDate.split('T')[0],
        reason: existingLeave.reason || '',
        attachmentUrl: existingLeave.attachmentUrl || '',
        note: existingLeave.note || '',
      });
      if (existingLeave.attachmentUrl) {
        // Extract filename from URL for display
        const urlParts = existingLeave.attachmentUrl.split('/');
        setUploadedFileName(urlParts[urlParts.length - 1] || 'Attached file');
      }
    }
  }, [existingLeave, reset]);

  const watchFromDate = watch('fromDate');
  const watchToDate = watch('toDate');
  const watchHalfDayEnabled = watch('halfDayEnabled');
  const watchLeaveTypeId = watch('leaveTypeId');
  const watchStaffId = watch('staffId');

  // Auto-disable half-day when From != To
  const isMultiDay = watchFromDate && watchToDate && watchFromDate !== watchToDate;
  useEffect(() => {
    if (isMultiDay && watchHalfDayEnabled) {
      setValue('halfDayEnabled', false);
      setValue('halfDay', null);
    }
  }, [isMultiDay, watchHalfDayEnabled, setValue]);

  // Auto-suggest returning date when toDate changes
  useEffect(() => {
    if (watchToDate && watchStaffId) {
      suggestReturningDate({ toDate: watchToDate, staffId: watchStaffId })
        .then(result => {
          setValue('returningDate', result.returningDate);
        })
        .catch(() => {
          // Fallback: toDate + 1 day
          const next = new Date(watchToDate);
          next.setDate(next.getDate() + 1);
          setValue('returningDate', next.toISOString().split('T')[0]);
        });
    }
  }, [watchToDate, watchStaffId, setValue]);

  // Check if selected LeaveType requires attachment
  const selectedLeaveType = leaveTypes?.find((lt: ILeaveType) => lt.id === watchLeaveTypeId);
  const showAttachmentHint = selectedLeaveType?.requiresAttachment && !watch('attachmentUrl');

  const createMut = useMutation({
    mutationFn: createLeave,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaves'] });
      toast({ title: 'Leave record created' });
      navigate('/leaves');
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: Parameters<typeof updateLeave>[1] }) => updateLeave(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaves'] });
      toast({ title: 'Leave record updated' });
      navigate('/leaves');
    },
  });

  async function onSubmit(values: LeaveFormValues, confirmOverlap = false) {
    try {
      const baseDto = {
        leaveTypeId: values.leaveTypeId,
        fromDate: values.fromDate,
        toDate: values.toDate,
        halfDay: values.halfDayEnabled ? (values.halfDay as 'AM' | 'PM') : null,
        returningDate: values.returningDate,
        reason: values.reason,
        attachmentUrl: values.attachmentUrl,
        note: values.note,
        confirmOverlap,
      };

      if (isEdit) {
        await updateMut.mutateAsync({ id: id!, dto: baseDto });
      } else {
        await createMut.mutateAsync({ ...baseDto, staffId: values.staffId });
      }
    } catch (err: any) {
      // E302: Overlap detected → show confirm dialog
      const errData = err?.response?.data;
      if (errData?.code === 'E302') {
        setPendingSubmitValues(values);
        setOverlapDialogOpen(true);
        return;
      }
      errToast(err, isEdit ? 'Failed to update' : 'Failed to create');
    }
  }

  async function handleOverlapConfirm() {
    if (pendingSubmitValues) {
      setOverlapDialogOpen(false);
      await onSubmit(pendingSubmitValues, true);
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/leaves')}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold" data-testid="page-title-leave-form">
            {isEdit ? 'Edit Leave Record' : 'Add Leave Record'}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit((v) => onSubmit(v))} className="space-y-4 bg-card border rounded-lg p-6">
        {/* Staff Search Select */}
        <div className="space-y-1">
          <Label>Staff *</Label>
          <Controller
            control={control}
            name="staffId"
            render={({ field }) => {
              const isDisabled = !!prefillStaffId || isEdit;
              let selectedLabel = '';
              if (isEdit && existingLeave) {
                selectedLabel = `${existingLeave.staff?.fullName ?? ''} (${existingLeave.staff?.employeeCode ?? ''})`;
              } else if (selectedEmployee) {
                selectedLabel = `${selectedEmployee.surname} ${selectedEmployee.firstName}${selectedEmployee.middleName ? ` ${selectedEmployee.middleName}` : ''} (${selectedEmployee.employeeId})`;
              }
              return (
                <div className="relative">
                  <Input
                    ref={staffInputRef}
                    data-testid="field-staff"
                    placeholder="Search by name or staff code..."
                    value={staffDropdownOpen ? staffSearch : selectedLabel}
                    disabled={isDisabled}
                    autoComplete="off"
                    onChange={(e) => {
                      if (!staffDropdownOpen && !isDisabled) {
                        setStaffDropdownOpen(true);
                      }
                      setStaffSearch(e.target.value);
                      if (e.target.value === '') {
                        field.onChange('');
                        setSelectedEmployee(null);
                      }
                    }}
                    onFocus={() => {
                      if (!isDisabled) {
                        if (staffBlurTimerRef.current) {
                          clearTimeout(staffBlurTimerRef.current);
                          staffBlurTimerRef.current = null;
                        }
                        setStaffSearch('');
                        setDebouncedSearch('');
                        setStaffDropdownOpen(true);
                      }
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                    onBlur={() => {
                      staffBlurTimerRef.current = setTimeout(() => {
                        setStaffDropdownOpen(false);
                        staffBlurTimerRef.current = null;
                      }, 150);
                    }}
                  />
                  {staffDropdownOpen && !isDisabled && (
                    <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-60 overflow-y-auto">
                      {staffFetching ? (
                        <p className="px-3 py-2 text-sm text-muted-foreground">Searching...</p>
                      ) : staffResults.length === 0 ? (
                        <p className="px-3 py-2 text-sm text-muted-foreground">No results found</p>
                      ) : (
                        staffResults.map((emp: IEmployee) => (
                          <button
                            key={emp.id}
                            type="button"
                            className="w-full text-left px-3 py-2 text-sm hover:bg-accent"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              field.onChange(emp.id);
                              setSelectedEmployee(emp);
                              setStaffDropdownOpen(false);
                              setStaffSearch('');
                            }}
                          >
                            {emp.surname} {emp.firstName}{emp.middleName ? ` ${emp.middleName}` : ''}{' '}
                            <span className="text-muted-foreground text-xs">({emp.employeeId})</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            }}
          />
          {errors.staffId && <p className="text-xs text-destructive">{errors.staffId.message}</p>}
        </div>

        {/* Leave Type */}
        <div className="space-y-1">
          <Label>Leave Type *</Label>
          <Controller
            control={control}
            name="leaveTypeId"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger data-testid="field-leave-type">
                  <SelectValue placeholder="Select leave type" />
                </SelectTrigger>
                <SelectContent>
                  {(leaveTypes ?? [])
                    .filter((lt: ILeaveType) => !lt.isDisabled)
                    .map((lt: ILeaveType) => (
                      <SelectItem key={lt.id} value={lt.id}>
                        <div className="flex items-center gap-2">
                          {lt.colorHex && (
                            <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: lt.colorHex }} />
                          )}
                          <span>{lt.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.leaveTypeId && <p className="text-xs text-destructive">{errors.leaveTypeId.message}</p>}
        </div>

        {/* Date Range */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="fromDate">From Date *</Label>
            <Input id="fromDate" type="date" {...register('fromDate')} data-testid="field-from-date" />
            {errors.fromDate && <p className="text-xs text-destructive">{errors.fromDate.message}</p>}
          </div>
          <div className="space-y-1">
            <Label htmlFor="toDate">To Date *</Label>
            <Input id="toDate" type="date" {...register('toDate')} data-testid="field-to-date" />
            {errors.toDate && <p className="text-xs text-destructive">{errors.toDate.message}</p>}
          </div>
        </div>

        {/* Half Day */}
        <div className="flex items-center justify-between">
          <Label>Half Day</Label>
          <Switch
            checked={watchHalfDayEnabled}
            onCheckedChange={(v) => {
              setValue('halfDayEnabled', v);
              if (!v) setValue('halfDay', null);
              else setValue('halfDay', 'AM');
            }}
            disabled={!!isMultiDay}
            data-testid="field-half-day-toggle"
          />
        </div>
        {watchHalfDayEnabled && (
          <div className="flex gap-4 ml-4">
            <label className="flex items-center gap-2">
              <input type="radio" value="AM" {...register('halfDay')} /> AM
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" value="PM" {...register('halfDay')} /> PM
            </label>
          </div>
        )}

        {/* Returning Date */}
        <div className="space-y-1">
          <Label htmlFor="returningDate">Returning Date *</Label>
          <Input id="returningDate" type="date" {...register('returningDate')} data-testid="field-returning-date" />
          <p className="text-xs text-muted-foreground">Auto-suggested (skips weekends & holidays). You can override.</p>
          {errors.returningDate && <p className="text-xs text-destructive">{errors.returningDate.message}</p>}
        </div>

        {/* Attachment Hint */}
        {showAttachmentHint && (
          <Alert className="border-yellow-300 bg-yellow-50" data-testid="hint-attachment">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-yellow-800">
              This leave type requires an attachment (e.g. medical certificate). Upload is recommended.
            </AlertDescription>
          </Alert>
        )}

        {/* Reason */}
        <div className="space-y-1">
          <Label htmlFor="reason">Reason</Label>
          <Textarea id="reason" {...register('reason')} rows={3} data-testid="field-reason" />
        </div>

        {/* Attachment Upload */}
        <div className="space-y-2">
          <Label>Attachment</Label>
          {watch('attachmentUrl') ? (
            <div className="flex items-center gap-2 rounded-md border px-3 py-2">
              <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <a
                href={watch('attachmentUrl')}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline truncate flex-1"
              >
                {uploadedFileName || 'Attached file'}
              </a>
              <button
                type="button"
                onClick={() => { setValue('attachmentUrl', ''); setUploadedFileName(null); }}
                className="text-muted-foreground hover:text-destructive"
                title="Remove attachment"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <label
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-md border border-dashed border-input text-sm cursor-pointer hover:border-primary hover:text-primary transition-colors ${
                  isUploading ? 'opacity-50 pointer-events-none' : ''
                }`}
              >
                {isUploading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
                {isUploading ? 'Uploading...' : 'Choose File'}
                <input
                  type="file"
                  accept={ACCEPT_STRING}
                  className="hidden"
                  disabled={isUploading}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    e.target.value = '';
                    try {
                      setIsUploading(true);
                      const publicUrl = await uploadFile(file, 'leave-attachments');
                      setValue('attachmentUrl', publicUrl);
                      setUploadedFileName(file.name);
                    } catch (err: any) {
                      toast({
                        title: 'Upload failed',
                        description: err?.message || 'Unable to upload file',
                        variant: 'destructive',
                      });
                    } finally {
                      setIsUploading(false);
                    }
                  }}
                />
              </label>
              <span className="text-xs text-muted-foreground">
                {ALLOWED_EXTENSIONS} — max {MAX_FILE_SIZE / 1024 / 1024}MB
              </span>
            </div>
          )}
        </div>

        {/* Note */}
        <div className="space-y-1">
          <Label htmlFor="note">Note</Label>
          <Textarea id="note" {...register('note')} rows={2} />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={() => navigate('/leaves')}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting} data-testid="btn-save">
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </form>

      {/* Overlap Confirmation Dialog (E302) */}
      <Dialog open={overlapDialogOpen} onOpenChange={setOverlapDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Overlap Detected</DialogTitle>
          </DialogHeader>
          <div className="px-6 pb-2">
            <p className="text-sm text-muted-foreground">
              This staff already has a leave record overlapping with the selected date range.
              Do you want to continue anyway?
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverlapDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleOverlapConfirm}>Confirm & Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
